import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
  faFilterList,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import {
  getBillingGroupsByAgreement,
  deleteBillingGroup,
} from "../../api/BillingGroups";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";

const BillingPage = () => {
  const { agreementPublicId, billingGroupPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const [billingGroups, setBillingGroups] = useState([]);
  const [filteredBillingGroups, setFilteredBillingGroups] = useState([]);
  const [selectedBillingGroup, setSelectedBillingGroup] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [billingGroupsLoaded, setBillingGroupsLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  const handleOpenModal = () => {
    if (!selectedBillingGroup || selectedBillingGroup.length === 0) {
        toast.error("No billing groups selected");
        return;
      }
    setIsModalOpen(true);
  };

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = billingGroups;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = billingGroups.filter(
          (billingGroup) =>
            billingGroup.userId?.toLowerCase().includes(searchLower) ||
          billingGroup.displayName?.toLowerCase().includes(searchLower) ||
          billingGroup.userPrincipalName?.toLowerCase().includes(searchLower) ||
          billingGroup.accountType?.toLowerCase().includes(searchLower) ||
          billingGroup.billingGroup?.toLowerCase().includes(searchLower)
        );
      }

      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = (a[sortColumn] || "").toString().toLowerCase();
          const bValue = (b[sortColumn] || "").toString().toLowerCase();
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      setFilteredBillingGroups(filtered);
    };

    applyFilterAndSort();
  }, [search, billingGroups, sortColumn, sortOrder]);

  useEffect(() => {
    const fetchBillingGroups = async () => {
      try {
        const accessToken = await getAccessToken();
        const groups = await getBillingGroupsByAgreement(agreementPublicId, accessToken);
        setBillingGroups(groups);
        setFilteredBillingGroups(groups);
        setBillingGroupsLoaded(true); // ✅ Ensure we update the loading state
      } catch (error) {
        console.error("Failed to fetch billing groups:", error);
        setBillingGroupsLoaded(true); // ✅ Set even on failure to avoid infinite loading
      }
    };
  
    fetchBillingGroups();
  }, [agreementPublicId, getAccessToken]);

  const handleDeleteBillingGroup = async (billingGroupPublicId) => {
    const toastId = toast.loading("Deleting billing group...");
    try {
      const accessToken = await getAccessToken();
      await deleteBillingGroup(billingGroupPublicId, accessToken);
      const newBillingGroups = await getBillingGroupsByAgreement(agreementPublicId, accessToken);
      setBillingGroups(newBillingGroups);
      
      toast.success("Billing group deleted", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });

    } catch (error) {
      toast.error("Failed to delete billing group", { id: toastId });
      console.error(error);
    }
  };

  console.log("AGREEMENT:", agreementPublicId);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Billing groups"} loading={!billingGroupsLoaded} />
            </div>

            <div className={`${!billingGroupsLoaded ? "loading" : ""} page-content`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Billing groups</h2>

                <Link to={`/${agreementPublicId}/billing-group/create`} className="btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create billing group
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div>
                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Manager, Email "}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredBillingGroups}
                    selectedRows={selectedBillingGroup} // Map selectedUsers to selectedRows
                    setSelectedRows={setSelectedBillingGroup} // Ensure selection updates correctly
                    noCheckbox={true}
                    noDelete={true}
                    noEdit={false}
                    columns={[
                      { key: "name", label: "Name" },
                      { key: "status", label: "Status" },
                      { key: "updatedBy", label: "Updated by" },
                      { key: "updatedDateTimeUTC", label: "Updated date" },                      
                    ]}
                    primaryKey="billingGroupPublicId" // Match this with billingGroupPublicId
                    basePath={`/${agreementPublicId}/billing-groups`} // Match this with the path to edit billing group
                    handleDelete={handleDeleteBillingGroup}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default BillingPage;
